import { useEffect, useState } from 'preact/hooks';
import { getMyEvents, type MyEventsResponse, CACHE_KEYS } from '../lib/api';
import { getCached, isCheckInQueued } from '../lib/cache';
import { EventCard } from '../components/EventCard';
import { Skeleton } from '../components/Skeleton';
import { useEventFlows } from './EventFlows';

interface EventosProps {
  /** Deep-link from sophi-bot DM: open this event's chat on mount. */
  initialChatEventId?: string;
  /** Called once after the deep-link is consumed so App can clear its state. */
  onConsumeInitialChat?: () => void;
}

export function Eventos({ initialChatEventId, onConsumeInitialChat }: EventosProps = {}) {
  const [data, setData] = useState<MyEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Modal + sub-screen orchestration is shared with Inicio — see EventFlows.tsx.
  const { setSelected, openChat, flows } = useEventFlows({ data, setData, reload: load });

  useEffect(() => { load(); }, []);

  // Once events are loaded, honor a deep-link from a sophi-bot DM and auto-open
  // the requested chat. Falls back gracefully if the event isn't in our list.
  useEffect(() => {
    if (!initialChatEventId || !data) return;
    const all = [...data.today, ...(data.pendingClosure || []), ...data.upcoming, ...data.completed];
    const found = all.find((e) => e.id === initialChatEventId);
    if (found) openChat(found);
    else {
      // Synthesize a minimal stub so the chat screen can still load by id.
      openChat({
        id: initialChatEventId,
        title: initialChatEventId,
        date: '', startTime: '', endTime: '', location: '',
        role: '', isCapitan: false, status: '', checkedIn: false,
      });
    }
    onConsumeInitialChat?.();
    // Intentionally only react to first data arrival; subsequent loads don't re-open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.today.length, data?.upcoming.length]);

  async function load() {
    setError(null);
    // Cache-first: paint last-known data instantly so the cold-open feels
    // sub-100ms instead of waiting for the network round-trip. Then refresh
    // in the background and replace.
    const cached = getCached<MyEventsResponse>(CACHE_KEYS.events);
    if (cached) {
      setData(cached.value);
      setLoading(false);
      setStale(true);
    } else {
      setLoading(true);
    }
    try {
      const r = await getMyEvents();
      setData(r);
      setStale(false);
    } catch (e) {
      // Only surface as error if we had nothing cached to show.
      if (!cached) setError((e as Error).message || 'No pudimos cargar tus eventos');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Mis eventos</h1></header>
        <div class="section-header">Hoy</div>
        <div class="card">
          {[0, 1].map((i) => (
            <div key={i} class="ledger-row">
              <div class="ledger-info" style={{ flex: 1 }}>
                <Skeleton width="70%" height={15} style={{ marginBottom: 6 }} />
                <Skeleton width="50%" height={12} style={{ marginBottom: 4 }} />
                <Skeleton width="40%" height={12} />
              </div>
            </div>
          ))}
        </div>
        <div class="section-header">Próximos</div>
        <div class="card">
          {[0, 1].map((i) => (
            <div key={i} class="ledger-row">
              <div class="ledger-info" style={{ flex: 1 }}>
                <Skeleton width="65%" height={15} style={{ marginBottom: 6 }} />
                <Skeleton width="45%" height={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  const today = data?.today || [];
  const pendingClosure = data?.pendingClosure || [];
  const upcoming = data?.upcoming || [];
  const completed = data?.completed || [];
  const isEmpty = today.length === 0 && pendingClosure.length === 0 && upcoming.length === 0 && completed.length === 0;
  // Surface any pending offline check-ins so staff sees their tap was captured
  // even before the queue drains. Quick scan of today's events; the queue is
  // typically 0 or 1 items so this is cheap.
  const queuedCount = today.filter(e => isCheckInQueued(e.id)).length;

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Eventos</h1>
        <div class="subtitle">
          {today.length} hoy
          {pendingClosure.length > 0 && ` · ${pendingClosure.length} sin cerrar`}
          {' '}· {upcoming.length} próximos · {completed.length} completados
          {stale && <span style={{ marginLeft: 8, opacity: 0.7 }}>· Actualizando…</span>}
        </div>
      </header>

      {queuedCount > 0 && (
        <div
          class="card"
          style={{
            background: 'var(--color-warning-bg, #fff8e1)',
            color: 'var(--color-text)',
            padding: '10px 14px',
            margin: '8px 0',
            fontSize: '0.92rem',
          }}
        >
          ⏳ {queuedCount === 1 ? 'Tu check-in está' : `${queuedCount} check-ins están`} pendiente{queuedCount === 1 ? '' : 's'} de sincronización. Se enviará automáticamente cuando recuperes conexión.
        </div>
      )}

      {isEmpty && (
        <div class="empty">
          <div class="ic">📅</div>
          <p>No tienes eventos asignados.</p>
        </div>
      )}

      {today.length > 0 && (
        <>
          <div class="section-header">Hoy</div>
          {today.map((e) => (
            <EventCard key={e.id} event={e} variant="today" onClick={() => setSelected(e)} />
          ))}
        </>
      )}

      {pendingClosure.length > 0 && (
        <>
          <div class="section-header">⚠️ Pendiente de cierre</div>
          {pendingClosure.map((e) => (
            <EventCard key={e.id} event={e} variant="unclosed" onClick={() => setSelected(e)} />
          ))}
        </>
      )}

      {upcoming.length > 0 && (
        <>
          <div class="section-header">Próximos</div>
          {upcoming.map((e) => (
            <EventCard key={e.id} event={e} onClick={() => setSelected(e)} />
          ))}
        </>
      )}

      {completed.length > 0 && (
        <>
          <div class="section-header">Completados</div>
          {completed.map((e) => (
            <EventCard key={e.id} event={e} variant="completed" onClick={() => setSelected(e)} />
          ))}
        </>
      )}

      {flows}
    </div>
  );
}
